import OpenInNew from "@mui/icons-material/OpenInNew";
import { Alert, Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { StatusChip } from "components/StatusChip";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { MouseEvent, useMemo } from "react";
import { Link } from "react-router-dom";

import {
  correctedToValue,
  correctionFieldLabel,
  correctionLineLabel,
  correctionPathLabel,
  correctionStateLabel,
  correctionValueLabel,
  isCorrectionLanded,
  ManifestCorrectionRow,
} from "./manifestCorrectionDisplay";

type Props = {
  rows: ManifestCorrectionRow[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  /** True when a search is narrowing the list — drives the empty-state copy. */
  searching: boolean;
  selectedIds: string[];
  /** Picks the current search (or the row cap) is hiding — they will not be cited. */
  hiddenSelectedCount: number;
  onSelectionChange: (ids: string[]) => void;
  /** Builds the per-row manifest link-outs. */
  tenantId: string;
};

/**
 * Warns that picks the grid isn't currently showing won't be cited. Only the
 * visible selection is submitted, so without this the count staff see and the
 * count that gets recorded quietly disagree.
 */
function hiddenSelectedText(visibleCount: number, hiddenCount: number): string {
  const total = visibleCount + hiddenCount;
  const subject = hiddenCount === 1 ? "1 is hidden" : `${hiddenCount} are hidden`;
  const object = hiddenCount === 1 ? "it" : "them";
  return `${total} selected — ${subject} by the current search and won't be cited. Clear the search to include ${object}.`;
}

/**
 * Candidate-corrections grid for "mark resolved → manifest correction".
 * Multi-select: one exceedance month can take several manifest fixes, and each is
 * its own audit record.
 *
 * Landed and in-flight corrections are listed together, told apart by the State
 * column — a fix set is realistically part done, and whether every pick has landed
 * is what decides between resolving the exceedance and parking it.
 *
 * Dates are UTC to match the Manifest Review page, where these same corrections
 * are worked, rather than the viewer's zone the rest of the dashboard uses.
 */
export function ManifestCorrectionPicker({
  rows,
  loading,
  search,
  onSearchChange,
  searching,
  selectedIds,
  hiddenSelectedCount,
  onSelectionChange,
  tenantId,
}: Props) {
  const columns = useMemo<CustomGridColDef<ManifestCorrectionRow>[]>(
    () => [
      { field: "manifestTrackingNumber", headerName: "Manifest", flex: 0.9, minWidth: 160 },
      {
        field: "shippedDate",
        headerName: "Shipped",
        flex: 0.5,
        valueFormatter: (value) => (value == null ? "" : getUtcDisplayDate(String(value))),
      },
      {
        field: "wasteLineNumber",
        headerName: "Line",
        flex: 0.4,
        valueGetter: (_, row) => correctionLineLabel(row),
      },
      {
        field: "field",
        headerName: "Field",
        flex: 0.7,
        valueGetter: (_, row) => correctionFieldLabel(row.field),
      },
      {
        field: "eManifestValue",
        headerName: "Corrected to",
        flex: 0.7,
        valueGetter: (_, row) => correctionValueLabel(correctedToValue(row)),
      },
      {
        field: "status",
        headerName: "State",
        flex: 0.6,
        valueGetter: (_, row) => correctionStateLabel(row),
        renderCell: (params) => (
          <StatusChip
            color={isCorrectionLanded(params.row) ? "success" : "info"}
            label={correctionStateLabel(params.row)}
          />
        ),
      },
      {
        field: "resolutionPath",
        headerName: "Corrected in",
        flex: 0.5,
        valueGetter: (_, row) => correctionPathLabel(row.resolutionPath),
      },
      {
        field: "resolvedAt",
        headerName: "Corrected",
        flex: 0.5,
        valueFormatter: (value) => (value == null ? "" : getUtcDisplayDate(String(value))),
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Open manifest in a new tab">
            <IconButton
              size="small"
              component={Link}
              to={`/o/${tenantId}/waste/manifests/${params.row.processedManifestId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open manifest in a new tab"
              onClick={(e: MouseEvent) => e.stopPropagation()}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tenantId]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        label="Search by manifest tracking number"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1 }}
      />
      {hiddenSelectedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {hiddenSelectedText(selectedIds.length, hiddenSelectedCount)}
        </Alert>
      )}
      <DataGrid<ManifestCorrectionRow>
        rows={rows}
        columns={columns}
        loading={loading}
        noRowsMessage={
          searching
            ? "No corrections match that tracking number."
            : "No manifest corrections for this facility in the affected period."
        }
        checkboxSelection
        rowSelectionModel={{ type: "include", ids: new Set(selectedIds) }}
        onRowSelectionModelChange={(model) => {
          onSelectionChange(model.type === "include" ? Array.from(model.ids).map(String) : []);
        }}
        pagination
        paginationMode="client"
        pageSizeOptions={[5, 10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
        sx={{ maxHeight: 360 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        Corrections on manifests shipped in the affected period, whichever side was fixed — the
        calculated status is the highest across e-Manifest and vendor data, so either can be the one
        that moved it. Check every correction that accounts for this discrepancy. Picking one that
        hasn&apos;t been corrected yet parks this discrepancy until it is.
      </Typography>
    </Box>
  );
}
